import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Headphones, FileText } from 'lucide-react';
import CrmCoreListPage from './components/CrmCoreListPage';
import { useCrmClientPartyPicker, useCrmSalesAgentsPicker } from './crmPickerOptions';
import { resolveClientPartySelection } from '../../shared/utils/crmCrossModuleUtils';

const SupportTicketsPage = () => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.crm.pages.supportTicketsPage.${key}`, opts);
  const { partyOptions, contacts, clients } = useCrmClientPartyPicker();
  const { agentOptions } = useCrmSalesAgentsPicker();

  const statusOptions = useMemo(() => [
    { value: 'open', label: p('status_open', { defaultValue: 'مفتوحة' }) },
    { value: 'in_progress', label: p('status_in_progress', { defaultValue: 'قيد المعالجة' }) },
    { value: 'resolved', label: p('status_resolved', { defaultValue: 'محلولة' }) },
    { value: 'closed', label: p('status_closed', { defaultValue: 'مغلقة' }) },
  ], [p]);

  const priorityOptions = useMemo(() => [
    { value: 'low', label: p('priority_low', { defaultValue: 'منخفضة' }) },
    { value: 'medium', label: p('priority_medium', { defaultValue: 'متوسطة' }) },
    { value: 'high', label: p('priority_high', { defaultValue: 'عالية' }) },
    { value: 'urgent', label: p('priority_urgent', { defaultValue: 'عاجلة' }) },
  ], [p]);

  const categoryOptions = useMemo(() => [
    { value: 'general', label: p('category_general', { defaultValue: 'عام' }) },
    { value: 'billing', label: p('category_billing', { defaultValue: 'فوترة' }) },
    { value: 'technical', label: p('category_technical', { defaultValue: 'تقني' }) },
    { value: 'product', label: p('category_product', { defaultValue: 'منتج' }) },
  ], [p]);

  const resolvePartyLabel = (partyKey, fallback = '') => {
    if (!partyKey) return fallback;
    return resolveClientPartySelection(partyKey, { contacts, clients }).displayName || fallback;
  };

  return (
    <CrmCoreListPage
      pageKey="supportTicketsPage"
      apiPath="/crm/support-tickets"
      icon={Headphones}
      entityRef={{ field: 'ticket_ref', prefixKey: 'interaction', fallback: 'TICK' }}
      statusOptions={statusOptions}
      mapRecord={(r) => ({
        ...r,
        contact_id: r.customer_id != null && r.customer_id !== '' ? String(r.customer_id) : '',
        customer_label: resolvePartyLabel(r.customer_id, r.customer_name) || '—',
        priority_label: priorityOptions.find((o) => o.value === r.priority)?.label || r.priority || '—',
        category_label: categoryOptions.find((o) => o.value === r.category)?.label || r.category || '—',
      })}
      buildPayload={(form) => {
        const party = resolveClientPartySelection(form.contact_id, { contacts, clients });
        const storedPartyKey = form.contact_id ? String(form.contact_id) : null;
        const customerId = party.contactId ? String(party.contactId) : storedPartyKey;
        return {
          subject: (form.subject || '').trim(),
          customer_id: customerId,
          customer_name: party.displayName || form.customer_name || null,
          description: form.description || '',
          priority: form.priority || 'medium',
          status: form.status || 'open',
          category: form.category || 'general',
          assigned_to: form.assigned_to || null,
          notes: form.notes || '',
        };
      }}
      formTabs={[
        { id: 'general', label: p('tab_general', { defaultValue: 'عام' }), icon: Headphones },
        { id: 'notes', label: p('tab_notes', { defaultValue: 'ملاحظات' }), icon: FileText, iconClassName: 'text-purple-400' },
      ]}
      fields={[
        { name: 'subject', labelKey: 'field_subject', required: true, tab: 'general' },
        { name: 'contact_id', labelKey: 'field_customer', type: 'select', options: partyOptions, tab: 'general' },
        { name: 'category', labelKey: 'field_category', type: 'select', options: categoryOptions, defaultValue: 'general', tab: 'general' },
        { name: 'priority', labelKey: 'field_priority', type: 'select', options: priorityOptions, defaultValue: 'medium', tab: 'general' },
        { name: 'status', labelKey: 'field_status', type: 'select', options: statusOptions, defaultValue: 'open', tab: 'general' },
        { name: 'assigned_to', labelKey: 'field_assigned', type: 'select', options: agentOptions, tab: 'general' },
        { name: 'description', labelKey: 'field_description', type: 'textarea', fullWidth: true, tab: 'general' },
        { name: 'notes', labelKey: 'field_notes', type: 'textarea', fullWidth: true, tab: 'notes' },
      ]}
      columns={[
        { key: 'ticket_ref', labelKey: 'col_ref' },
        { key: 'subject', labelKey: 'col_subject' },
        { key: 'customer_label', labelKey: 'col_customer' },
        { key: 'category_label', labelKey: 'col_category' },
        { key: 'priority_label', labelKey: 'col_priority' },
        { key: 'status', labelKey: 'col_status', type: 'status' },
        { key: 'created_at', labelKey: 'col_created', type: 'date' },
      ]}
    />
  );
};

export default SupportTicketsPage;
